import { ArrowRight, Briefcase, FileText, GraduationCap, User } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './ParsedDataReview.css';

interface ParsedDataReviewProps {
  data: any;
  onBack: () => void;
}

const ParsedDataReview: React.FC<ParsedDataReviewProps> = ({ data, onBack }) => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsCreating(true);
    setError(null);

    try {
      const response = await api.createJourneyWithParsedData(data);
      console.log('Journey created with parsed data:', response);

      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Navigate to the conversation stages (Projects stage)
      navigate('/journey/stages', {
        state: {
          journeyId: response.journey.id,
          uploadMode: true
        }
      });
    } catch (err: any) {
      console.error('Failed to create journey:', err);
      setError(err.response?.data?.error || 'Failed to create journey. Please try again.');
      setIsCreating(false);
    }
  };

  const { personal, summary, education, employment, projects, skills, certifications } = data;

  return (
    <div className="parsed-review-container">
      <div className="parsed-review-header">
        <h1>Review Your Extracted Data</h1>
        <p className="parsed-review-subtitle">
          We've extracted this information from your resume. Review it and confirm to continue.
        </p>
      </div>

      <div className="parsed-review-content">
        {/* Personal Information */}
        {personal && (
          <div className="parsed-section">
            <h2>
              <User size={20} />
              Personal Information
            </h2>
            <div className="parsed-grid">
              <div className="parsed-item">
                <label>Name</label>
                <p>{personal.name || 'Not provided'}</p>
              </div>
              <div className="parsed-item">
                <label>Email</label>
                <p>{personal.email || 'Not provided'}</p>
              </div>
              <div className="parsed-item">
                <label>Phone</label>
                <p>{personal.phone || 'Not provided'}</p>
              </div>
              <div className="parsed-item">
                <label>Location</label>
                <p>{personal.location || 'Not provided'}</p>
              </div>
              {personal.linkedin && (
                <div className="parsed-item">
                  <label>LinkedIn</label>
                  <p className="link-text">{personal.linkedin}</p>
                </div>
              )}
              {personal.github && (
                <div className="parsed-item">
                  <label>GitHub</label>
                  <p className="link-text">{personal.github}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div className="parsed-section">
            <h2>
              <FileText size={20} />
              Professional Summary
            </h2>
            <p className="parsed-summary">{summary}</p>
          </div>
        )}

        {/* Education */}
        {education && education.length > 0 && (
          <div className="parsed-section">
            <h2>
              <GraduationCap size={20} />
              Education
            </h2>
            {[...education]
              .sort((a: any, b: any) => {
                // Sort by end date (or start date if no end date) in descending order (latest first)
                const dateA = a.endDate || a.startDate || '';
                const dateB = b.endDate || b.startDate || '';
                return dateB.localeCompare(dateA);
              })
              .map((edu: any, index: number) => (
              <div key={index} className="parsed-card">
                <h3>{edu.degree} in {edu.field}</h3>
                <p className="parsed-institution">{edu.institution}</p>
                <p className="parsed-dates">
                  {edu.startDate} - {edu.endDate || 'Present'}
                </p>
                {edu.gpa && <p className="parsed-gpa">GPA: {edu.gpa}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Employment */}
        {employment && employment.length > 0 && (
          <div className="parsed-section">
            <h2>
              <Briefcase size={20} />
              Work Experience
            </h2>
            {[...employment]
              .sort((a: any, b: any) => {
                // Sort by end date (or current, or start date) in descending order (latest first)
                const dateA = a.isCurrent ? '9999-12-31' : (a.endDate || a.startDate || '');
                const dateB = b.isCurrent ? '9999-12-31' : (b.endDate || b.startDate || '');
                return dateB.localeCompare(dateA);
              })
              .map((job: any, index: number) => (
              <div key={index} className="parsed-card">
                <h3>{job.position}</h3>
                <p className="parsed-company">{job.company} • {job.location}</p>
                <p className="parsed-dates">
                  {job.startDate} - {job.isCurrent ? 'Present' : job.endDate}
                </p>
                <p className="parsed-description">{job.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Projects */}
        {projects && projects.length > 0 && (
          <div className="parsed-section">
            <h2>🚀 Projects</h2>
            {projects.map((project: any, index: number) => (
              <div key={index} className="parsed-card">
                <h3>{project.name}</h3>
                <p className="parsed-description">{project.description}</p>
                {project.technologies && project.technologies.length > 0 && (
                  <div className="parsed-technologies">
                    {project.technologies.map((tech: string, i: number) => (
                      <span key={i} className="tech-badge">{tech}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Skills */}
        {skills && skills.length > 0 && (
          <div className="parsed-section">
            <h2>⚡ Skills</h2>
            <div className="parsed-skills">
              {skills.map((skill: string, index: number) => (
                <span key={index} className="skill-badge">{skill}</span>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {certifications && certifications.length > 0 && (
          <div className="parsed-section">
            <h2>🏆 Certifications</h2>
            {certifications.map((cert: any, index: number) => (
              <div key={index} className="parsed-card">
                <h3>{cert.name}</h3>
                <p className="parsed-issuer">{cert.issuer}</p>
                {cert.issueDate && (
                  <p className="parsed-dates">Issued: {cert.issueDate}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="parsed-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <div className="parsed-actions">
        <button className="btn-back" onClick={onBack} disabled={isCreating}>
          ← Upload Different Resume
        </button>
        <button 
          className="btn-confirm" 
          onClick={handleConfirm}
          disabled={isCreating}
        >
          {isCreating ? 'Creating...' : (
            <>
              Continue
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>

      <div className="parsed-note">
        <p>
          💡 <strong>What's next?</strong> We'll dive deep into your projects and achievements 
          with targeted questions to make your resume stand out!
        </p>
      </div>
    </div>
  );
};

export default ParsedDataReview;

